import i18n from '@/libs/i18n'

/**
 * The API's error codes — `pms-api/pkg/enums/Error`, key by key.
 *
 * The SAME service the reception portal talks to, so the same numbers: this console is
 * a second window and never a second product. Only the codes a screen here can actually
 * meet are listed; the rest fall through to the server's own sentence, which is the
 * right reading for anything this portal has no better words for.
 */
export enum Errors {
    InternalServerError = 1,
    InvalidData         = 2,
    NotFound            = 3,
    Unauthorized        = 4,
    FailedToInsert      = 5,
    AlreadyExists       = 6,
    Forbidden           = 7,
    IncorrectStatus     = 8,
}

/** The API's own code, for a caller that has to DO something rather than say something. */
export const parseErrorCode = (errorObject: unknown): number =>
    (errorObject as { error?: { code?: number } } | null)?.error?.code ?? 0

/**
 * The server's own sentence, verbatim. Empty when the body carries none.
 *
 * A bare-string body (`PrintHTTP.WriteErrorMessage`: `{"error": "..."}`) carries no
 * `message` field for the object form below to read — see `pms-web`'s `libs/error.ts`
 * for the fuller account.
 */
export const parseErrorMessage = (errorObject: unknown): string => {
    const raw = (errorObject as { error?: unknown } | null)?.error

    if (typeof raw === 'string') return raw

    return (raw as { message?: string } | undefined)?.message ?? ''
}

/**
 * Reads through `i18n.global` rather than `useI18n()` so it stays callable outside a
 * component setup context — a fetch handler is not a component.
 *
 * The default case reads the server's own message before falling back to the generic
 * sentence: a refusal that names a module, a house or a company is worth more than any
 * sentence this portal could write in its place.
 */
export const parseError = (errorObject: unknown, fallback = ''): string => {
    const t = i18n.global.t

    switch (parseErrorCode(errorObject)) {
        case Errors.InvalidData:       return t('errors.invalid_data')
        case Errors.NotFound:          return t('errors.not_found')
        case Errors.Unauthorized:      return t('errors.unauthorized')
        case Errors.Forbidden:         return t('errors.forbidden')
        case Errors.AlreadyExists:     return t('errors.already_exists')
        default:                       return parseErrorMessage(errorObject) || fallback || t('errors.generic_error')
    }
}
